import React, { useState } from "react";
import {
  Box,
  TextField,
  IconButton,
  Container,
  InputAdornment,
  Button,
  CircularProgress,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import axios from "axios";
import Message from "../components/Message";

const predefinedMessages = [
  "Create a workout plan",
  "Activities to make friends in new city",
  "Plan a relaxing day",
  "Study vocabulary",
];

const ChatArea: React.FC = () => {
  const [messages, setMessages] = useState<string[]>([]);
  const [input, setInput] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [showPredefinedMessages, setShowPredefinedMessages] =
    useState<boolean>(true);

  const handleSend = async (message: string) => {
    if (message.trim() === "") return;

    setMessages([...messages, `You: ${message}`]);
    setInput("");
    setShowPredefinedMessages(false);
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/generate-content`,
        {
          prompt: message,
        }
      );
      const { generatedContent } = response.data;
      setMessages((prevMessages) => [...prevMessages, `${generatedContent}`]);
    } catch (error) {
      console.error("Error generating content:", error);
      setMessages((prevMessages) => [
        ...prevMessages,
        "Error: Failed to generate response",
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handlePredefinedMessageClick = (message: string) => {
    handleSend(message);
  };

  const handleEdit = (index: number, newText: string) => {
    const updatedMessages = [...messages];
    updatedMessages[index] = newText;
    setMessages(updatedMessages);
  };

  return (
    <Container
      maxWidth="md"
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        padding: 0,
      }}
    >
      <Box
        flex={1}
        display="flex"
        flexDirection="column"
        p={2}
        sx={{
          overflowY: "scroll",
          msOverflowStyle: "none", // Hide scrollbar in Internet Explorer and Edge
          scrollbarWidth: "none", // Hide scrollbar in Firefox
          "&::-webkit-scrollbar": { display: "none" }, // Hide scrollbar in WebKit browsers
        }}
      >
        {messages.map((msg, index) => (
          <Message key={index} text={msg} index={index} onEdit={handleEdit} />
        ))}
        {loading && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            mt={2}
          >
            <CircularProgress />
          </Box>
        )}
      </Box>
      {showPredefinedMessages && (
        <Box display="flex" justifyContent="center" alignItems="center" mb={2}>
          {predefinedMessages.map((msg, index) => (
            <Button
              key={index}
              onClick={() => handlePredefinedMessageClick(msg)}
              variant="outlined"
              sx={{ margin: 1, padding: 2, borderRadius: "10px" }}
            >
              {msg}
            </Button>
          ))}
        </Box>
      )}
      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSend(input);
        }}
        p={2}
        sx={{
          display: "flex",
          alignItems: "center",
          bgcolor: "#f0f0f0",
          borderRadius: "50px",
          mb: 2,
          position: "sticky",
          bottom: 0,
        }}
      >
        <IconButton>
          <AttachFileIcon />
        </IconButton>
        <TextField
          value={input}
          onChange={(e) => setInput(e.target.value)}
          variant="outlined"
          placeholder="Message ChatGPT"
          fullWidth
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "50px",
              backgroundColor: "#f0f0f0",
              padding: "5px 15px",
              "& fieldset": {
                border: "none",
              },
              "& .MuiInputBase-input": {
                padding: "10px 0",
                fontSize: "16px",
              },
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  color="primary"
                  onClick={() => handleSend(input)}
                  disabled={!input}
                >
                  <SendIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
    </Container>
  );
};

export default ChatArea;
